import React from 'react';
import { Loader } from 'lucide-react';

type Props = {
  initial?: any;
  onCancel?: () => void;
  onSubmit: (data: any) => Promise<void> | void;
};

const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

export default function IncomeForm({ initial, onCancel, onSubmit }: Props) {
  const [form, setForm] = React.useState(() => ({
    name: initial?.name || '',
    amount: initial?.amount ?? 0,
    description: initial?.description || '',
    month: initial?.month || new Date().getMonth() + 1,
    year: initial?.year || new Date().getFullYear(),
  }));
  const [isLoading, setIsLoading] = React.useState(false);

  const [displayAmount, setDisplayAmount] = React.useState(() =>
    form.amount ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(form.amount) : ''
  );

  const handleChange = (key: string, value: any) => setForm((s) => ({ ...s, [key]: value }));

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[\D]/g, '');
    if (!value) {
      setDisplayAmount('');
      handleChange('amount', 0);
      return;
    }
    const numeric = Number(value) / 100;
    setDisplayAmount(new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numeric));
    handleChange('amount', numeric);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      await onSubmit(payload);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-900">Nome</label>
        <input
          placeholder="Ex: Salário, Vale Alimentação, Freelance"
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
          required
        />
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-900">Valor</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="0,00"
            value={displayAmount}
            onChange={handleAmountChange}
            className="w-full border border-gray-300 pl-10 pr-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
            required
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-900">Descrição (opcional)</label>
        <input
          placeholder="Detalhes adicionais"
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-900">Mês</label>
          <select
            value={form.month}
            onChange={(e) => handleChange('month', Number(e.target.value))}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none text-gray-900 bg-white"
          >
            {monthNames.map((m, idx) => (
              <option key={m} value={idx + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-900">Ano</label>
          <select
            value={form.year}
            onChange={(e) => handleChange('year', Number(e.target.value))}
            className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none text-gray-900 bg-white"
          >
            {[2023, 2024, 2025, 2026].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 font-medium disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <Loader className="w-4 h-4 animate-spin" />}
          {isLoading ? 'Processando...' : (initial ? 'Salvar' : 'Criar')}
        </button>
      </div>
    </form>
  );
}
